import React, { useState } from 'react';
import { Box, HStack, VStack, Menu, Pressable, useTheme } from 'native-base';
import { useTranslation } from 'react-i18next';
import { ChevronDown, RotateCcw } from 'lucide-react';

import { Text } from '@pimm/base';
import { ResetConfirmationModal, ResetConfirmationAction } from './reset-confirmation-modal';

type ResetOption = {
  action: ResetConfirmationAction;
  renderIcon: (color: string) => React.ReactNode;
  title: string;
  enabledDescription: string;
  disabledDescription: string;
  isEnabled: boolean;
};

type PopoverResetMenuProps = {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  hasAutoAssigned: boolean;
  hasAutoScheduled: boolean;
  onConfirmReset: (action: ResetConfirmationAction) => void;
};

export const PopoverResetMenu = ({ isOpen, onOpen, onClose, hasAutoAssigned, hasAutoScheduled, onConfirmReset }: PopoverResetMenuProps) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [confirmingAction, setConfirmingAction] = useState<ResetConfirmationAction | null>(null);

  const resetOptions: ResetOption[] = [
    {
      action: 'assignments',
      renderIcon: color => <RotateCcw size={16} color={color} />,
      title: 'cleaning_task:reset_assignments',
      enabledDescription: 'cleaning_task:remove_all_auto_assigned_employees',
      disabledDescription: 'cleaning_task:no_auto_assignments_in_current_view',
      isEnabled: hasAutoAssigned,
    },
    {
      action: 'schedules',
      renderIcon: color => <RotateCcw size={16} color={color} />,
      title: 'cleaning_task:reset_schedules',
      enabledDescription: 'cleaning_task:remove_all_auto_scheduled_times_and_assignments',
      disabledDescription: 'cleaning_task:no_auto_schedules_in_current_view',
      isEnabled: hasAutoScheduled,
    },
  ];

  const handleConfirm = () => {
    if (!confirmingAction) return;
    onConfirmReset(confirmingAction);
    setConfirmingAction(null);
  };

  return (
    <>
      <Menu
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
        placement="bottom right"
        width={290}
        p={0}
        mt={1}
        borderWidth={1}
        borderColor={hasAutoAssigned || hasAutoScheduled ? 'red.300' : 'gray.300'}
        borderRadius="md"
        overflow="hidden"
        trigger={triggerProps => (
          <Pressable {...triggerProps}>
            <HStack
              px={3}
              py={2}
              space={1}
              alignItems="center"
              bg={hasAutoAssigned || hasAutoScheduled ? 'red.50' : 'gray.50'}
              borderWidth={1}
              borderColor={hasAutoAssigned || hasAutoScheduled ? 'red.400' : 'gray.200'}
              borderRadius="md"
            >
              <RotateCcw size={18} color={hasAutoAssigned || hasAutoScheduled ? colors.error[700] : colors.gray[500]} />
              <Text size="md" fontWeight={600} color={hasAutoAssigned || hasAutoScheduled ? colors.error[700] : colors.gray[500]}>
                {t('cleaning_task:reset')}
              </Text>
              <ChevronDown size={12} color={hasAutoAssigned || hasAutoScheduled ? colors.error[700] : colors.gray[500]} />
            </HStack>
          </Pressable>
        )}
      >
        {resetOptions.map(({ action, renderIcon, title, enabledDescription, disabledDescription, isEnabled }, index) => {
          const isLast = index === resetOptions.length - 1;

          return (
            <Box
              flex={1}
              key={action}
              py={0}
              bg="white"
              borderBottomWidth={isLast ? 0 : 1}
              borderColor={isLast ? 'transparent' : 'gray.100'}
            >
              <Menu.Item
                onPress={() => setConfirmingAction(action)}
                isDisabled={!isEnabled}
                px={0}
                py={2}
                _hover={{ bg: 'transparent' }}
                _pressed={{ bg: 'transparent' }}
                _focus={{ bg: 'transparent' }}
              >
                <VStack w="full">
                  <HStack space={3} alignItems="center" py={1}>
                    <Box
                      w={7}
                      h={7}
                      rounded="md"
                      bg={hasAutoAssigned || hasAutoScheduled ? 'red.100' : 'gray.100'}
                      alignItems="center"
                      justifyContent="center"
                      opacity={isEnabled ? 1 : 0.5}
                    >
                      {renderIcon(isEnabled ? colors.error[700] : colors.gray[400])}
                    </Box>
                    <VStack flex={1}>
                      <Text size="md" fontWeight={600} color={isEnabled ? 'red.700' : 'gray.400'}>
                        {t(title)}
                      </Text>
                      <Text size="sm" color={isEnabled ? colors.gray[700] : colors.gray[400]}>
                        {t(isEnabled ? enabledDescription : disabledDescription)}
                      </Text>
                    </VStack>
                  </HStack>
                </VStack>
              </Menu.Item>
            </Box>
          );
        })}
      </Menu>

      <ResetConfirmationModal action={confirmingAction} onConfirm={handleConfirm} onClose={() => setConfirmingAction(null)} />
    </>
  );
};

export default PopoverResetMenu;
